import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { DialogClose, QlfButton } from '@qualifio/ui-commons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScheduleTemplate, useScheduledTemplate } from '../../hooks/use-templates';

interface SchedulePublicationDialogProps {
  open: boolean;
  templateId: string;
  existingPublicationDate?: Date;
  onClose: () => void;
  onConfirmWithExisting?: (newTemplateId: string, newScheduledAt: Date) => void;
}

export function SchedulePublicationDialog({
  open,
  templateId,
  existingPublicationDate,
  onClose,
  onConfirmWithExisting,
}: SchedulePublicationDialogProps) {
  const { t } = useTranslation();
  const { mutateAsync: scheduleTemplate } = useScheduleTemplate();
  const { data: scheduledTemplate } = useScheduledTemplate();

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<Date | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      if (existingPublicationDate) {
        setSelectedDate(existingPublicationDate);
        setSelectedTime(existingPublicationDate);
      } else {
        setSelectedDate(null);
        setSelectedTime(null);
      }
      setDateError(null);
    }
  }, [open, existingPublicationDate]);

  const combinedDateTime = useMemo(() => {
    if (!selectedDate || !selectedTime) return null;

    const combined = new Date(selectedDate);
    combined.setHours(selectedTime.getHours());
    combined.setMinutes(selectedTime.getMinutes());
    combined.setSeconds(0);
    combined.setMilliseconds(0);

    return combined;
  }, [selectedDate, selectedTime]);

  const validateDateTime = useCallback(() => {
    if (!combinedDateTime) {
      setDateError(null);
      return false;
    }

    const now = new Date();
    if (combinedDateTime <= now) {
      setDateError(t('editor.template.version.schedule.dialog.error.past'));
      return false;
    }

    setDateError(null);
    return true;
  }, [combinedDateTime, t]);

  useEffect(() => {
    if (combinedDateTime) {
      validateDateTime();
    }
  }, [combinedDateTime, validateDateTime]);

  const isValid = useMemo(() => {
    return combinedDateTime !== null && combinedDateTime > new Date();
  }, [combinedDateTime]);

  const handleClearAll = useCallback(() => {
    setSelectedDate(null);
    setSelectedTime(null);
    setDateError(null);
  }, []);

  const handleClose = useCallback(() => {
    handleClearAll();
    onClose();
  }, [handleClearAll, onClose]);

  const handleSave = useCallback(async () => {
    if (!combinedDateTime || !isValid) return;

    // Check if there's an existing scheduled version for a different template
    if (scheduledTemplate && scheduledTemplate.id !== templateId && onConfirmWithExisting) {
      onConfirmWithExisting(templateId, combinedDateTime);
      return;
    }

    await scheduleTemplate({ templateId, publicationDate: combinedDateTime });
    handleClose();
  }, [
    combinedDateTime,
    isValid,
    scheduledTemplate,
    templateId,
    onConfirmWithExisting,
    scheduleTemplate,
    handleClose,
  ]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogClose close={handleClose} />
      <DialogTitle
        sx={theme => ({
          padding: theme.spacing(4, 8, 1),
          textAlign: 'center',
          '&:after': {
            backgroundColor: theme.palette.primary.main,
            content: "''",
            display: 'block',
            height: 3,
            margin: theme.spacing(1, 'auto', 0),
            width: 40,
          },
        })}
      >
        {existingPublicationDate
          ? t('editor.template.version.schedule.dialog.title.edit')
          : t('editor.template.version.schedule.dialog.title')}
      </DialogTitle>
      <DialogContent>
        <Typography
          sx={theme => ({
            textAlign: 'center',
            color: 'text.secondary',
            marginBottom: theme.spacing(3),
          })}
        >
          {t('editor.template.version.schedule.dialog.description')}
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            sx={theme => ({
              display: 'flex',
              alignItems: 'flex-start',
              gap: theme.spacing(2),
              marginBottom: theme.spacing(1),
            })}
          >
            <DatePicker
              value={selectedDate}
              onChange={setSelectedDate}
              minDate={new Date()}
              inputFormat="dd/MM/yyyy"
              renderInput={params => <TextField {...params} error={!!dateError} sx={{ flex: 1 }} />}
            />
            <TimePicker
              value={selectedTime}
              onChange={setSelectedTime}
              ampm
              renderInput={params => <TextField {...params} error={!!dateError} sx={{ flex: 1 }} />}
            />
          </Box>
          {(!!selectedDate || !!selectedTime) && (
            <Box sx={theme => ({ marginBottom: theme.spacing(2) })}>
              <Button
                sx={theme => ({
                  color: 'secondary.main',
                  textTransform: 'none',
                  minWidth: 'auto',
                  padding: theme.spacing(1),
                })}
                onClick={handleClearAll}
                disabled={!selectedDate && !selectedTime}
                variant="text"
              >
                {t('editor.template.version.schedule.dialog.clear')}
              </Button>
            </Box>
          )}
        </LocalizationProvider>

        {dateError && (
          <Typography color="error" variant="body2">
            {dateError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <QlfButton.Cancel onClick={handleClose} />
        <QlfButton.Save onClick={handleSave} disabled={!isValid} />
      </DialogActions>
    </Dialog>
  );
}
